import React, { FC, useState, useEffect, useRef, useMemo } from 'react';
import {
	Box,
	TextField,
	Button,
	Grid,
	CircularProgress,
	Snackbar,
	IconButton,
	Divider,
	Dialog,
	DialogTitle,
	DialogContent,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Message from './Message';
import generateConversationId from './Api/ConversationService';
import { useGlobalState } from '../GlobalStateContext';
import Shortcuts from './Shortcuts';
import { getSecurityQuestion } from './Api/securityQuestion';
import { v4 as uuidv4 } from 'uuid';
import chatStream from './Api/chatStreamService';
import chatAgent from './Api/chatAgent';
import chatFlow from './Api/chatFlow';
import { AppTypeList } from './Api/getListTypeApplication';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import useSpeechRecognition from '../Hooks/useSpeechRecognition';
import useTimeout from '../Hooks/useTimeout';
import ChatSeparator from './ChatSeparator';
import { extractUUID } from '../utils/uuidfinder';
import CloseIcon from '@mui/icons-material/Close';
import 'animate.css';
import useWindowDimensions from '../Hooks/useWindowDimensions';
import HubspotForm from './HubspotFormComponent/HubspotFormComponent';

interface ChatMessage {
	id: string;
	text: string;
	active_form: boolean;
	sender: 'bot' | 'user' | 'chat' | 'internal';
	time: string;
	date: Date;
	showIcon: boolean;
	traceId?: string;
	isStreaming?: boolean;
}

interface ChatUIProps {
	appType: string;
	typeList: AppTypeList[];
	setShowChat: (value: boolean) => void;
	setInput: (value: string) => void;
	input: string;
	showChat: boolean;
	inputPlaceholder: string;
}

const ChatUI: FC<ChatUIProps> = ({ appType, typeList, setShowChat, setInput, input, showChat, inputPlaceholder }) => {
	const { state: globalState } = useGlobalState();
	const chatbotSettings = globalState?.chatbotSettings;
	const chatbotShortcuts = globalState?.chatbotShortcuts;
	const temperature = chatbotSettings?.temperature || '0.7';
	const organizationId = chatbotSettings?.organization_id;
	const [isLoading, setIsLoading] = useState(false);
	const [isChatLoading, setIsChatLoading] = useState(false);
	const [isLoadingSend, setIsLoadingSend] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const conversationIdRef = useRef<string | null>(null);
	const messageContainerRef = useRef<HTMLDivElement | null>(null);
	const [securityQuestionCorrect, setSecurityQuestionCorrect] =
		useState(true);
	const [shortcutsEnabled, setShortcutsEnabled] = useState(true);
	const [formAlreadySent, setFormAlreadySent] = useState(false);
	const [charLimitSnackbarOpen, setCharLimitSnackbarOpen] = useState(false);
	const [charLimitMessage, setCharLimitMessage] = useState('');
	const [visibleDate, setVisibleDate] = useState<Date | null>(null);
	const [showVolumeIcon, setShowVolumeIcon] = useState(true);
	const [agentId, setAgentId] = useState('');
	const [agentAliasId, setAgentAliasId] = useState('');
	const [flowId, setFlowId] = useState('');
	const [flowAliasId, setFlowAliasId] = useState('');
	const [showContactForm, setShowContactForm] = useState(false);
	const windowDimensions = useWindowDimensions();
	const isMobile = windowDimensions.width < 768;
	const MAX_CHARS = 20000;

	const [formData, setFormData] = useState({
		question: '',
		encrypted_answer: '',
	});

	const initialId = uuidv4();

	const obtenerHora = () => {
		const now = new Date();
		return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
	};

	const IsIconVisible = () => {
		return showVolumeIcon && (typingStatus === 'Done Typing' || typingStatus === 'Initializing' ) && !isChatLoading;
	};

	// const chatbotSettingsId = chatbotSettings.id;

	const urlSearchParams = new URLSearchParams(window.location.search);
	const chatbotId = extractUUID(
		urlSearchParams.get('chatbot_id') ||
		urlSearchParams.get('application_id')
	);
	const limits = Object.fromEntries(urlSearchParams);
	delete limits['chatbot_id'];
	delete limits['application_id'];
	const enableVolumeIcon = chatbotSettings?.is_voice_enabled === true;

	const requiresSecurityQuestion = chatbotSettings?.is_security_question_required === true;

	useEffect(() => {
		if (appType === 'AgentApplication' || appType === 'FlowApplication' && typeList) {
			const appInfo = typeList.find((item) => item.app_id === chatbotId);
			if (appInfo) {
				if (appType === 'AgentApplication') {
					if (appInfo.agent_id) {
						setAgentId(appInfo.agent_id);
					}
					if (appInfo.active_alias_id) {
						setAgentAliasId(appInfo.active_alias_id);
					}
				}
				if (appType === 'FlowApplication') {
					if (appInfo.flow_id) {
						setFlowId(appInfo.flow_id);
					}
					if (appInfo.active_alias_id) {
						setFlowAliasId(appInfo.active_alias_id);
					}
				}
			}
		}
	}, [typeList, appType]);

	useEffect(() => {
		const fetchAndSetSecurityQuestion = async (id: string) => {
			try {
				const questionAnswer = await getSecurityQuestion(id);
				const question = questionAnswer[0].question;
				const encrypted_answer = questionAnswer[0].encrypted_answer;

				setFormData({ question, encrypted_answer });

				if (requiresSecurityQuestion) {
					setMessages([
						{
							id: initialId,
							text: question,
							active_form: false,
							sender: 'bot',
							time: obtenerHora(),
							date: new Date(),
							showIcon: enableVolumeIcon
						},
					]);
					setSecurityQuestionCorrect(false);
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			}
		};

		if (requiresSecurityQuestion) {
			if (chatbotId && chatbotId !== '') {
				fetchAndSetSecurityQuestion(chatbotId);
			}
		}
	}, [chatbotId]);

	useEffect(() => {
		if (!requiresSecurityQuestion) {
			if (
				chatbotShortcuts &&
				chatbotShortcuts?.welcome_message?.length > 0
			) {
				setMessages([
					{
						id: initialId,
						text: chatbotShortcuts?.welcome_message[0].message,
						active_form: false,
						sender: 'bot',
						time: obtenerHora(),
						date: new Date(),
						showIcon: enableVolumeIcon
					},
				]);
			}
		}
	}, [requiresSecurityQuestion, chatbotShortcuts]);

	const getFirstVisibleMessage = () => {
		const container = messageContainerRef.current;
		if (container){
			const messagesElements = container.querySelectorAll('div');
	
			for (let i = 0; i < messagesElements.length; i++) {
				const messageElement = messagesElements[i];
						
				const rect = messageElement.getBoundingClientRect();
				if (rect.top >= 0 && rect.top <= container.clientHeight) {
					return messages[i];
				}
			}
		}
		return null;
	};

	const shortcuts = useMemo(() => {
		return chatbotShortcuts?.shortcut_message?.length > 0
			? chatbotShortcuts?.shortcut_message.map(
				(item: any) => item.message,
			)
			: [];
	}, [chatbotShortcuts]);

	const [isAutoScrolling, setIsAutoScrolling] = useState<boolean>(true);

	useEffect(() => {
		const messageContainer = messageContainerRef.current;

		if (!messageContainer) {
			return;
		}

		const handleMutation = () => {
			if (isAutoScrolling) {
				messageContainer.scrollTop = messageContainer.scrollHeight;
			}
		};

		const observer = new MutationObserver(handleMutation);

		observer.observe(messageContainer, {
			childList: true,
			subtree: true,
		});

		return () => observer.disconnect();
	}, [isAutoScrolling]);

	const [clickedAtTop, setClickedAtTop] = useState(false);

	const formatDate = (date: Date) => {
		const today = new Date();
		const isSameDay = today.toDateString() === date.toDateString();
		
		const yesterday = new Date(today);
		yesterday.setDate(today.getDate() - 1);
		const isYesterday = yesterday.toDateString() === date.toDateString();
	
		const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
	
		if (isSameDay) {
			return `Hoy, ${date.toLocaleDateString('es-ES', options)}`;
		} else if (isYesterday) {
			return `Ayer, ${date.toLocaleDateString('es-ES', options)}`;
		} else {
			return date.toLocaleDateString('es-ES', options);
		}
	};

	const handleScroll = () => {
		const messageContainer = messageContainerRef.current;

		if (!messageContainer) {
			return;
		}

		const { scrollTop, scrollHeight, clientHeight } = messageContainer;
		const atBottom = scrollTop + clientHeight === scrollHeight;

		setIsAutoScrolling(atBottom);

		if (!atBottom || clickedAtTop) {
			setShowScrollButton(true);
		} else {
			setShowScrollButton(false);
		}
		const firstVisibleMessage = getFirstVisibleMessage();
		if (firstVisibleMessage) {
			setVisibleDate(firstVisibleMessage.date);
		}
	};

	const handleManualScroll = () => {
		setClickedAtTop(false);
		if (isAutoScrolling) {
			setIsAutoScrolling(false);
			setTimeout(() => {
				setIsAutoScrolling(true);
			}, 100);
		}
	};

	const handleScrollBottom = () => {
		if (messageContainerRef.current) {
			messageContainerRef.current.scrollTo({
				top: messageContainerRef.current.scrollHeight,
				behavior: 'smooth',
			});
			setIsAutoScrolling(true);
			setClickedAtTop(false);
		}
	};

	const [showScrollButton, setShowScrollButton] = useState(false);

	const chatStreamFn = (botUniqueKey: string, text: string, convId: string) => {
		chatStream(
			text,
			convId,
			chatbotId,
			temperature,
			limits,
		)
			.then((res) => {
				const headers = res.data?.headers?.get('content-type');
				if (headers?.includes('stream')) {
					let openContactForm = false;
					let chatText = '';
					const stream = res.data.body;
					if (stream) {
						const reader = stream.getReader();
						const readChunk = () => {
							reader
								.read()
								.then(({ value, done }: any) => {
									setShortcutsEnabled(false);
									setShowVolumeIcon(false);
									if (done) {
										setShortcutsEnabled(true);
										setShowVolumeIcon(true);
										setMessages((m) => {
											return m.map((msg) => {
												if (msg.id === botUniqueKey) {
													msg.isStreaming = false;
												}
												return msg;
											});
										});
										return;
									}
									setIsChatLoading(false);
									const chunkString =
										new TextDecoder().decode(
											value,
										);
									if (
										chunkString !== 'undefined'
									) {
										chatText += chunkString;
										if (
											chatText.includes(
												'#contactar',
											)
										) {
											chatText =
												chatText.replaceAll(
													'#contactar',
													'',
												);
											if (
												!openContactForm
											) {
												openContactForm =
													true;
											}
										}
										setMessages((m) =>{
											return m.map((msg) => {
												if (
													msg.id ===
													botUniqueKey
												) {
													msg.text =
														chatText;
													msg.active_form =
														openContactForm;
													msg.traceId = res.traceId;
													msg.isStreaming = true;
												}
												return msg;
											});},
										);
										// setTimeout(() => {
										// 	if (
										// 		messageContainerRef.current
										// 	) {
										// 		if (
										// 			messageContainerRef
										// 				.current
										// 				.scrollTop !==
										// 			messageContainerRef
										// 				.current
										// 				.scrollHeight
										// 		) {
										// 			const form =
										// 				document.getElementById(
										// 					'chatLeadForm',
										// 				);
										// 			const scrollHt =
										// 				form
										// 					? messageContainerRef
										// 						.current
										// 						.scrollHeight -
										// 						form.clientHeight *
										// 							1.5
										// 					: messageContainerRef
										// 						.current
										// 						.scrollHeight;
										// 			messageContainerRef.current.scrollTo(
										// 				{
										// 					top: scrollHt,
										// 					behavior:
										// 						'smooth',
										// 				},
										// 			);
										// 		}
										// 	}
										// }, 500);
									}
									readChunk();
								})
								.catch((error: any) => {
									console.error(error);
									const errorMessage = 'Ha ocurrido un error, vuelve a intentar';
									
									setMessages((prevMessages) => [
										...prevMessages,
										{
											id: uuidv4(),
											text: errorMessage,
											active_form: false,
											sender: 'bot',
											time: obtenerHora(),
											date: new Date(),
											showIcon: enableVolumeIcon
										},
									]);
									
									setSnackbarOpen(true);
									setIsChatLoading(false);
									setShortcutsEnabled(true);
									setShowVolumeIcon(true);
								});
						};
						readChunk();
						return res;
					}
				} else {
					const errorMessage = 'Ha ocurrido un error, vuelve a intentar';
					setMessages((m) =>
						m.map((msg) => {
							if (msg.id === botUniqueKey) {
								msg.text = errorMessage;
								msg.active_form = false;
							}
							return msg;
						}),
					);
					setIsChatLoading(false);
				}
			})
			.then((res) => {
				start(+res.timeToLive * 60 * 1000);
			})
			.catch((error: any) => {
				console.error(error);
				const errorMessage = 'Ha ocurrido un error, vuelve a intentar';
				
				setMessages((prevMessages) => [
					...prevMessages,
					{
						id: uuidv4(),
						text: errorMessage,
						active_form: false,
						sender: 'bot',
						time: obtenerHora(),
						date: new Date(),
						showIcon: enableVolumeIcon
					},
				]);
				
				setSnackbarOpen(true);
				setIsChatLoading(false);
				setShortcutsEnabled(true);
				setShowVolumeIcon(true);
			});
	};

	const chatAgentFn = (botUniqueKey: string, text: string, convId: string) => {
		chatAgent(
			text,
			convId,
			agentId,
			agentAliasId,
			chatbotId,
		)
			.then((res) => {
				let openContactForm = false;
				let chatText = '';
				if (res.message) {
					const errorMessage = 'Ha ocurrido un error, vuelve a intentar';
					setMessages((prevMessages) => [
						...prevMessages,
						{
							id: uuidv4(),
							text: errorMessage,
							active_form: false,
							sender: 'bot',
							time: obtenerHora(),
							date: new Date(),
							showIcon: enableVolumeIcon
						},
					]);
					setIsChatLoading(false);
					return;
				}

				clear();
				const stream = res.data.response;
				if (stream) {
					setIsChatLoading(false);
					chatText = stream;
					if (chatText.includes('#contactar')) {
						chatText = chatText.replaceAll(
							'#contactar',
							'',
						);
						if (!openContactForm) {
							openContactForm = true;
						}
					}
					setMessages((m) =>
						m.map((msg) => {
							if (msg.id === botUniqueKey) {
								msg.text = stream;
								msg.active_form = false;
								msg.traceId = res.traceId;
							}
							return msg;
						}),
					);
				} else {
					setIsChatLoading(false);
					setMessages((m) =>
						m.map((msg) => {
							if (msg.id === botUniqueKey) {
								msg.text = JSON.stringify(res);
								msg.active_form = false;
							}
							return msg;
						}),
					);
				}
				return res;
			})
			.then((res) => {
				start(+res.timeToLive * 60 * 1000);
			})
			.catch((error: any) => {
				console.error(error);
				const errorMessage = 'Ha ocurrido un error, vuelve a intentar';

				setMessages((prevMessages) => [
					...prevMessages,
					{
						id: uuidv4(),
						text: errorMessage,
						active_form: false,
						sender: 'bot',
						time: obtenerHora(),
						date: new Date(),
						showIcon: enableVolumeIcon
					},
				]);

				setSnackbarOpen(true);
				setIsChatLoading(false);
				setShortcutsEnabled(true);
				setShowVolumeIcon(true);
			});
	};

	const chatFlowFn = (botUniqueKey: string, text: string, convId: string) => {
		chatFlow(
			text,
			convId,
			flowId,
			flowAliasId,
			chatbotId,
		)
			.then((res) => {
				if (res.message) {
					const errorMessage = 'Ha ocurrido un error, vuelve a intentar';
					setMessages((prevMessages) => [
						...prevMessages,
						{
							id: uuidv4(),
							text: errorMessage,
							active_form: false,
							sender: 'bot',
							time: obtenerHora(),
							date: new Date(),
							showIcon: enableVolumeIcon
						},
					]);
					setIsChatLoading(false);
					return;
				}
				
				let openContactForm = false;
				let chatText = '';
				const stream = res.data;

				if (stream) {
					setIsChatLoading(false);
					chatText = stream.flowOutputEvent.content.document;
					if (chatText.includes('#contactar')) {
						chatText = chatText.replaceAll('#contactar', '');
						if (!openContactForm) {
							openContactForm = true;
						}
					}
					setMessages((m) =>
						m.map((msg) => {
							if (msg.id === botUniqueKey) {
								msg.text = chatText;
								msg.active_form = false;
								msg.traceId = res.traceId;
							}
							return msg;
						}),
					);
					conversationIdRef.current = res.conversationId;
					return res;
				}
			})
			.then((res) => {
				start(+res.timeToLive * 60 * 1000);
			})
			.catch((error: any) => {
				console.error(error);
				const errorMessage = 'Ha ocurrido un error, vuelve a intentar';
				
				setMessages((prevMessages) => [
					...prevMessages,
					{
						id: uuidv4(),
						text: errorMessage,
						active_form: false,
						sender: 'bot',
						time: obtenerHora(),
						date: new Date(),
						showIcon: enableVolumeIcon
					},
				]);
				
				setSnackbarOpen(true);
				setIsChatLoading(false);
				setShortcutsEnabled(true);
				setShowVolumeIcon(true);
			});
		setInput('');
		setIsChatLoading(true);
	};

	const handleSend = async (text: string) => {
		if (text && text.trim() !== '') {
			setIsLoading(true);
			setIsLoadingSend(true);
			setShortcutsEnabled(false);
			setShowVolumeIcon(false);
			clear();

			if(clicked) {
				stopListening();
				resetTranscript();
				setClicked(false);
			}

			const uniqueKey = uuidv4();

			setTimeout(() => {
				if (messageContainerRef.current) {
					messageContainerRef.current.scrollTo({
						top: messageContainerRef.current.scrollHeight,
						behavior: 'smooth',
					});
				}
			}, 100);

			setMessages((prevMessages) => [
				...prevMessages,
				{
					id: uniqueKey,
					text: text,
					active_form: false,
					sender: 'user',
					time: obtenerHora(),
					date: new Date(),
					showIcon: enableVolumeIcon
				},
			]);

			try {
				if (!conversationIdRef.current && chatbotId && organizationId) {
					const data = await generateConversationId(
						chatbotId,
						text,
						organizationId,
					);
					conversationIdRef.current = data.payload.conversation_id;

					if (data.payload?.token_for_auth) {
						setSecurityQuestionCorrect(true);
						const defaultWelcomeMessage =
							chatbotShortcuts?.welcome_message &&
							chatbotShortcuts?.welcome_message?.length > 0
								? chatbotShortcuts?.welcome_message[0]?.message
								: '¡Bienvenido!';
						setMessages((prevMessages) => [
							...prevMessages,
							{
								id: initialId,
								text: defaultWelcomeMessage,
								active_form: false,
								sender: 'bot',
								time: obtenerHora(),
								date: new Date(),
								showIcon: enableVolumeIcon
							},
						]);
						setInput('');
					}

					if (!conversationIdRef.current) {
						setMessages((prevMessages) => [
							...prevMessages,
							{
								id: initialId,
								text: 'Respuesta incorrecta. Intenta de nuevo',
								active_form: false,
								sender: 'bot',
								time: obtenerHora(),
								date: new Date(),
								showIcon: enableVolumeIcon
							},
						]);
						setInput('');
					}
				}

				if (
					conversationIdRef.current &&
					chatbotId &&
					securityQuestionCorrect
				) {
					setIsChatLoading(true);
					const botUniqueKey = uuidv4();
					setMessages((prevMessages) => [
						...prevMessages,
						{
							id: botUniqueKey,
							text: '',
							active_form: false,
							sender: 'bot',
							time: obtenerHora(),
							date: new Date(),
							showIcon: enableVolumeIcon
						},
					]);
					switch (appType) {
					case 'FlowApplication':
						chatFlowFn(botUniqueKey, text, conversationIdRef.current);
						break;
					case 'AgentApplication':
						chatAgentFn(botUniqueKey, text, conversationIdRef.current);
						break;
					case 'SearchApplication':
						chatStreamFn(botUniqueKey, text, conversationIdRef.current);
						break;
					}
					setInput('');
				} else if (!chatbotId) {
					console.error(
						'No se encontró el parámetro chatbot_id en la URL',
					);
				}
			} catch (error) {
				if (error) {
					setSnackbarOpen(true);
				}
			} finally {
				setIsLoading(false);
				setIsLoadingSend(false);
				setShortcutsEnabled(true);
				setShowVolumeIcon(true);
			}
		}
	};

	const handleShortcutClick = async (shortcutText: string) => {
		setInput(shortcutText);
		
		handleSend(shortcutText);
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		const isPaste = event.nativeEvent && (event.nativeEvent as InputEvent).inputType === 'insertFromPaste';
		if (newValue.length <= MAX_CHARS) {
			setInput(newValue);
		} else if (!isPaste) {
			setCharLimitMessage(`Tu mensaje supera el límite de ${MAX_CHARS} caracteres. ¿Podrías intentarlo de nuevo?`);
			setCharLimitSnackbarOpen(true);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			
			handleSend(input);
		}
	};
	const [typingStatus, setTypingStatus] = useState('Initializing');

	useEffect(() => {
		setMessages((prevItems) => {
			const updatedItems = [...prevItems];
			const lastItemIndex = updatedItems.length - 1;
			updatedItems[lastItemIndex] = { 
				...updatedItems[lastItemIndex], 
				showIcon: IsIconVisible()
			};
			return updatedItems;
		});
	}, [typingStatus, showVolumeIcon, isChatLoading]);

	const handleTypingStatusChange = (newStatus: string) => {
		setTypingStatus(newStatus);
	};

	const sendSuggestion = (selected: string) => {
		handleSend(selected);
	};

	//#region Speech-to-Text
	const { startListening, stopListening, transcript, resetTranscript } =
		useSpeechRecognition();

	useEffect(() => {
		if (transcript.length <= MAX_CHARS) {
			setInput(transcript);
			handleSend(transcript);
		} else {
			setCharLimitMessage(`El mensaje de voz no debe superar los ${MAX_CHARS} caracteres.`);
			setCharLimitSnackbarOpen(true);
			setInput(transcript.substring(0, MAX_CHARS));
		}
	}, [transcript]);

	const [clicked, setClicked] = useState(false);

	const toggleMicrophone = () => {
		if (clicked) {
			stopListening();
		} else {
			resetTranscript();
			startListening();
		}
		setClicked(!clicked);
	};
	//#endregion

	const handlePaste = (event: React.ClipboardEvent) => {
		const pastedText = event.clipboardData.getData('text');
		if (pastedText.length + input.length > MAX_CHARS) {
			event.preventDefault();
			setCharLimitMessage('El texto que estás pegando es muy largo. ¡Intenta con uno más corto!');
			setCharLimitSnackbarOpen(true);
		}
	};

	const {start, clear} = useTimeout(() => {
		setMessages((prevState) => [...prevState, {
			id: uuidv4(),
			date: new Date(),
			text: 'Esta conversación ha caducado debido a la inactividad. Empezaremos una nueva conversación, ¿en qué puedo ayudarte? 😊',
			time: obtenerHora(),
			sender: 'internal',
			active_form: false,
			showIcon: enableVolumeIcon
		}]);
	});

	const resetChat = () => {
		setMessages([]);	
		setInput('');	
		conversationIdRef.current = null;	
		setIsLoading(false);
		setIsChatLoading(false);
		setIsLoadingSend(false);	
		setSnackbarOpen(false);
		setCharLimitSnackbarOpen(false);	
		setSecurityQuestionCorrect(true);	
		setFormAlreadySent(false);	
		setVisibleDate(null);	
		setAgentId('');
		setAgentAliasId('');
		setFlowId('');
		setFlowAliasId('');	
		setShortcutsEnabled(true);
		setIsAutoScrolling(true);
		setShowScrollButton(false);
		setClicked(false);
		setTypingStatus('Initializing');
		setShowVolumeIcon(true);
		clear();
		if (typeof stopListening === 'function') stopListening();
		if (typeof resetTranscript === 'function') resetTranscript();
	};

	const handleCloseButton = () => {
		setShowChat(false);
		resetChat();
	};

	useEffect(() => {
		if (showChat) {
			handleSend(input);
		}
	}, [showChat]);

	const demoBoxStyle = {
		paddingTop: '60px',
		height: 'calc(100% - 120px)',
		backgroundColor: 'transparent',
		display: 'flex',
		flexDirection: 'column',
		flexWrap: 'nowrap',
		overflowY: 'hidden',
		position: 'relative',
	};

	return (
		<Box
			sx={demoBoxStyle}
		>
			<Box
				sx={{
					flexGrow: 1,
					overflowY: 'auto',
					pl: 2,
					pr: 2,
					pt: 4,
					backgroundColor: 'transparent',
					animationDuration: '2s'
				}}
				ref={messageContainerRef}
				onScroll={handleScroll}
				onTouchStart={handleManualScroll}
				onMouseDown={handleManualScroll}
				className={showChat ? 'animate__animated animate__slideInDown' : 'animate__animated animate__slideOutUp'}
			>
				{messages.map((message) => {
					if (message.sender === 'internal') {
						return (
							<ChatSeparator key={message.id} text={message.text} />
						);
					} else {
						return <Message
							key={message.id}
							message={message}
							conversationId={conversationIdRef.current}
							isLoading={isLoading}
							isChatLoading={isChatLoading}
							botMessageBgColor={'#F1F3F3'}
							botMessageColor={'#000000'}
							userMessageBgColor={chatbotSettings?.user_message_color}
							userMessageColor={'#ffffff'}
							onTypingStatusChange={handleTypingStatusChange}
							setFormAlreadySent={setFormAlreadySent}
							formAlreadySent={formAlreadySent}
							appId={chatbotId ?? ''}
							organizationId={organizationId || ''}
							time={message.time}
							showIconVolume={message.showIcon}
							shortcutsEnabled={shortcutsEnabled}
							typingStatus={typingStatus}
							setSuggestionSelected={sendSuggestion}
							isStreaming={message.isStreaming}
							setShowContactForm={setShowContactForm}
						/>;
					}
				})}
			</Box>
			<Box
				sx={{
					pr: 2,
					pl: 2,
					pt: 2,
					pb: 0,
					backgroundColor: 'transparent',
				}}
			>
				<Box sx={{ marginBottom: '1em' }}>
					<Shortcuts
						shortcuts={shortcuts}
						onShortcutClick={handleShortcutClick}
						shortcutsEnabled={
							shortcutsEnabled &&
							typingStatus === 'Done Typing' &&
							!isChatLoading
						}
					/>
				</Box>
				<Grid container spacing={0} sx={{ animationDuration: '1s' }} className={showChat ? 'animate__animated animate__slideInUp' : ''}>
					<Grid item xs={12}>
						<Grid 
							container 
							spacing={2} 
							style={{ paddingBottom: '16px', justifyContent: 'space-between' }}
						>
							<Grid item style={{flexBasis: 'calc(100% - 64px)' }}>
								<TextField
									size="small"
									fullWidth
									placeholder={inputPlaceholder}
									variant="outlined"
									value={input}
									onChange={handleInputChange}
									onPaste={handlePaste}
									disabled={isLoading || typingStatus == 'Typing'}
									autoComplete={'off'}
									onKeyDown={handleKeyDown}
									inputProps={{
										maxLength: MAX_CHARS+1
									}}
									sx={{
										'& .MuiInputBase-root': {
											borderRadius: '40px',
											backgroundColor: 'white',
											'&:hover':{
												'& .MuiOutlinedInput-notchedOutline': {
													borderColor: 'rgba(127, 17, 224, 1)'
												}
											}
										},
										'& .MuiOutlinedInput-notchedOutline': {
											borderColor: 'rgba(127, 17, 224, 0.6)',
										}
									}}
									InputProps={{
										endAdornment: (
											<Button
												disabled={
													isLoading ||
													isLoadingSend ||
													typingStatus == 'Typing'
												}
												style={{
													backgroundColor:
														chatbotSettings?.button_color ||
														'green',
													color: 'white',
													width: '2rem',
													height: '2rem',
													borderRadius: '50%',
													minWidth: 'unset',
													margin: '11px 0px 11px 11px',
													position: 'relative',
												}}
												onClick={() => {
													handleSend(input);
												}}
											>
												{isLoadingSend ||
												typingStatus == 'Typing' ? (
														<CircularProgress
															color="inherit"
															size={24}
															style={{
																position: 'absolute',
															}}
														/>
													) : (
														<ArrowUpwardIcon />
													)}
											</Button>
										),
									}}
								/>
							</Grid>
							<Grid item style={{ flexBasis: '64px' }}
							>
								<IconButton
									color={clicked ? 'error' : 'default'}
									size='large'
									style={{
										position: 'absolute',
									}}
									onClick={toggleMicrophone}
								>
									<KeyboardVoiceIcon />
								</IconButton>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Box>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={6000}
				onClose={() => setSnackbarOpen(false)}
			>
				<MuiAlert
					elevation={6}
					variant="filled"
					severity="error"
					onClose={() => setSnackbarOpen(false)}
				>
					Error al contactar con el servidor. Inténtalo de nuevo más
					tarde.
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={charLimitSnackbarOpen}
				autoHideDuration={3000}
				onClose={() => setCharLimitSnackbarOpen(false)}
			>
				<MuiAlert
					elevation={6}
					variant="filled"
					severity="info"
					onClose={() => setCharLimitSnackbarOpen(false)}
				>
					{charLimitMessage}
				</MuiAlert>
			</Snackbar>
			<Button
				onClick={handleCloseButton}
				sx={{
					position: 'absolute',
					top: isMobile ? 10 : 10,
					right: isMobile ? 10 : 0,
					zIndex: 4,
					backgroundColor: 'rgba(118, 10, 168, 0.1)',
					color: '#760aa8',
					borderRadius: '50%',
					width: 36,
					height: 36,
					minWidth: 'unset',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					animationDelay: '5s',
					'&:hover':{
						backgroundColor: 'rgba(118, 10, 168, 0.2)',
						color: '#760aa8',
					}
				}}
				className="animate__animated animate__fadeIn"
			>
				<CloseIcon />
			</Button>
			{false && showScrollButton && (
				<Button
					onClick={handleScrollBottom}
					style={{
						position: 'fixed',
						bottom: 190,
						right: 20,
						zIndex: 4,
						backgroundColor: '#000',
						color: '#fff',
						borderRadius: '50%',
						width: 40,
						height: 40,
						minWidth: 'unset',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<ArrowDownwardIcon />
				</Button>
			)}
			<Dialog
				open={showContactForm}
				onClose={() => setShowContactForm(false)}
				maxWidth={'sm'}
				sx={{
					'& .MuiDialog-paper': {
						width: '100%',
						maxWidth: '600px',
						height: '90vh',
					},
					'& .MuiBackdrop-root': {
						backgroundColor: 'rgba(0, 0, 0, 0)'
					}
				}}
			>
				<DialogTitle>Formulario de contacto</DialogTitle>
				<IconButton
					aria-label="close"
					onClick={() => setShowContactForm(false)}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						backgroundColor: 'rgba(118, 10, 168, 0.1)',
						color: '#760aa8',
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<HubspotForm></HubspotForm>
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default ChatUI;
